import { useState } from "react";
import {
  FileSignature,
  Plus,
  Trash2,
  ExternalLink,
  RefreshCw,
  CheckCircle2,
  Clock,
  X,
  Copy,
  AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type Signer = {
  id: string;
  name: string;
  surname: string;
  email: string;
  phone: string;
};

const STATUS_LABELS: Record<
  string,
  { label: string; color: string; icon: typeof Clock }
> = {
  draft: { label: "Draft", color: "bg-zinc-100 text-zinc-700", icon: Clock },
  sent: { label: "Sent", color: "bg-blue-100 text-blue-700", icon: Clock },
  partially_signed: {
    label: "Partially signed",
    color: "bg-amber-100 text-amber-800",
    icon: Clock,
  },
  completed: {
    label: "Signed",
    color: "bg-emerald-100 text-emerald-800",
    icon: CheckCircle2,
  },
  archived: {
    label: "Archived",
    color: "bg-emerald-100 text-emerald-800",
    icon: CheckCircle2,
  },
  cancelled: { label: "Cancelled", color: "bg-zinc-100 text-zinc-600", icon: X },
  failed: { label: "Failed", color: "bg-rose-100 text-rose-700", icon: AlertCircle },
};

export function OpportunitySigning({ opportunityId }: { opportunityId: string }) {
  const utils = trpc.useUtils();
  const configQuery = trpc.signing.isConfigured.useQuery();
  const listQuery = trpc.signing.list.useQuery({ opportunityId });
  const documentsQuery = trpc.documents.list.useQuery({ opportunityId });
  const [open, setOpen] = useState(false);

  const refresh = trpc.signing.refresh.useMutation({
    onSuccess: () => {
      utils.signing.list.invalidate({ opportunityId });
      toast.success("Refreshed");
    },
    onError: (e) => toast.error(e.message ?? "Refresh failed"),
  });

  const cancel = trpc.signing.cancel.useMutation({
    onSuccess: () => {
      utils.signing.list.invalidate({ opportunityId });
      toast.success("Signing cancelled");
    },
    onError: (e) => toast.error(e.message ?? "Cancel failed"),
  });

  const items = listQuery.data ?? [];
  const configured = configQuery.data?.configured ?? false;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium uppercase tracking-wide text-muted-foreground">
          <FileSignature className="h-3.5 w-3.5" />
          E-signing (Dokobit)
        </CardTitle>
        <Button
          size="sm"
          variant="outline"
          className="h-7 gap-1 text-xs"
          onClick={() => setOpen(true)}
          disabled={!configured}
          title={configured ? "Send a document for signing" : "DOKOBIT_API_KEY not configured"}
        >
          <Plus className="h-3 w-3" />
          Send for signing
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {!configured && (
          <p className="text-xs text-muted-foreground">
            Set <code className="font-mono">DOKOBIT_API_KEY</code> in env to enable Dokobit
            e-signing.
          </p>
        )}
        {listQuery.isLoading ? (
          <Skeleton className="h-12 w-full" />
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No signing requests yet.</p>
        ) : (
          items.map((row) => (
            <SigningRow
              key={row.id}
              row={row}
              onRefresh={() => refresh.mutate({ id: row.id })}
              onCancel={() => cancel.mutate({ id: row.id })}
              refreshing={refresh.isPending && refresh.variables?.id === row.id}
            />
          ))
        )}
      </CardContent>

      <CreateSigningDialog
        open={open}
        onOpenChange={setOpen}
        opportunityId={opportunityId}
        documents={
          documentsQuery.data?.map((d) => ({
            id: d.id,
            name: d.name,
            url: d.documentUrl,
          })) ?? []
        }
        onCreated={() => {
          utils.signing.list.invalidate({ opportunityId });
          setOpen(false);
        }}
      />
    </Card>
  );
}

function SigningRow({
  row,
  onRefresh,
  onCancel,
  refreshing,
}: {
  row: {
    id: string;
    status: string;
    documentName: string;
    dokobitSigningToken: string | null;
    signedFileUrl: string | null;
    signers: Array<{
      name: string;
      surname: string;
      email?: string;
      status?: string;
      signerAccessToken?: string;
      signedAt?: string;
    }>;
    createdAt: Date | string;
    completedAt: Date | string | null;
    error: string | null;
  };
  onRefresh: () => void;
  onCancel: () => void;
  refreshing: boolean;
}) {
  const meta = STATUS_LABELS[row.status] ?? STATUS_LABELS.draft!;
  const Icon = meta.icon;
  const isTerminal =
    row.status === "completed" ||
    row.status === "archived" ||
    row.status === "cancelled" ||
    row.status === "failed";

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{row.documentName}</span>
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                meta.color
              )}
            >
              <Icon className="h-3 w-3" />
              {meta.label}
            </span>
          </div>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Created {new Date(row.createdAt).toLocaleString("en-US")}
            {row.completedAt
              ? ` · Signed ${new Date(row.completedAt).toLocaleString("en-US")}`
              : ""}
          </p>
          {row.error ? (
            <p className="mt-1 text-xs text-rose-600">{row.error}</p>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={onRefresh}
            disabled={refreshing || isTerminal}
            title="Refresh from Dokobit"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
          </Button>
          {!isTerminal && row.dokobitSigningToken ? (
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={onCancel}
              title="Cancel signing"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </div>

      {row.signers.length > 0 ? (
        <ul className="space-y-1.5 border-t pt-2">
          {row.signers.map((s, i) => (
            <li key={`${s.name}-${i}`} className="flex items-center justify-between gap-2 text-xs">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="truncate font-medium">
                    {s.name} {s.surname}
                  </span>
                  {s.status === "signed" ? (
                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                  ) : (
                    <Clock className="h-3 w-3 text-muted-foreground" />
                  )}
                </div>
                {s.email ? (
                  <span className="truncate text-[10px] text-muted-foreground">{s.email}</span>
                ) : null}
              </div>
              {s.signerAccessToken && row.dokobitSigningToken && s.status !== "signed" ? (
                <SignerLinkButton
                  signingToken={row.dokobitSigningToken}
                  accessToken={s.signerAccessToken}
                />
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      {row.signedFileUrl ? (
        <a
          href={row.signedFileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          <ExternalLink className="h-3 w-3" />
          Download signed document
        </a>
      ) : null}
    </div>
  );
}

function SignerLinkButton({
  signingToken,
  accessToken,
}: {
  signingToken: string;
  accessToken: string;
}) {
  const configQuery = trpc.signing.isConfigured.useQuery();
  const base = configQuery.data?.baseUrl ?? "";
  if (!base) return null;
  const url = `${base}/signing/${encodeURIComponent(signingToken)}?access_token=${encodeURIComponent(accessToken)}`;

  return (
    <div className="flex items-center gap-1">
      <Button asChild size="sm" variant="outline" className="h-6 gap-1 px-2 text-[10px]">
        <a href={url} target="_blank" rel="noopener noreferrer">
          <ExternalLink className="h-3 w-3" />
          Open
        </a>
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-6 w-6"
        onClick={() => {
          navigator.clipboard.writeText(url);
          toast.success("Link copied");
        }}
        title="Copy signer link"
      >
        <Copy className="h-3 w-3" />
      </Button>
    </div>
  );
}

function CreateSigningDialog({
  open,
  onOpenChange,
  opportunityId,
  documents,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  opportunityId: string;
  documents: Array<{ id: string; name: string; url: string | null }>;
  onCreated: () => void;
}) {
  const [documentId, setDocumentId] = useState<string>("");
  const [documentName, setDocumentName] = useState<string>("");
  const [sourceFileUrl, setSourceFileUrl] = useState<string>("");
  const [language, setLanguage] = useState<"lt" | "en">("lt");
  const [signers, setSigners] = useState<Signer[]>([
    { id: "", name: "", surname: "", email: "", phone: "" },
  ]);

  const reset = () => {
    setDocumentId("");
    setDocumentName("");
    setSourceFileUrl("");
    setLanguage("lt");
    setSigners([{ id: "", name: "", surname: "", email: "", phone: "" }]);
  };

  const create = trpc.signing.create.useMutation({
    onSuccess: () => {
      toast.success("Sent for signing");
      reset();
      onCreated();
    },
    onError: (e) => toast.error(e.message ?? "Failed to create signing"),
  });

  const handleDocumentSelect = (id: string) => {
    setDocumentId(id);
    const doc = documents.find((d) => d.id === id);
    if (doc) {
      setDocumentName(doc.name);
      if (doc.url) setSourceFileUrl(doc.url);
    }
  };

  const handleSubmit = () => {
    const cleanSigners = signers
      .map((s) => ({
        id: s.id.trim(),
        name: s.name.trim(),
        surname: s.surname.trim(),
        email: s.email.trim() || undefined,
        phone: s.phone.trim() || undefined,
      }))
      .filter((s) => s.id && s.name && s.surname);

    if (cleanSigners.length === 0) {
      toast.error("Add at least one signer with personal code, name and surname");
      return;
    }
    if (!sourceFileUrl.trim()) {
      toast.error("Document URL is required");
      return;
    }
    if (!documentName.trim()) {
      toast.error("Document name is required");
      return;
    }

    create.mutate({
      opportunityId,
      documentId: documentId || undefined,
      sourceFileUrl: sourceFileUrl.trim(),
      documentName: documentName.trim(),
      signers: cleanSigners,
      language,
      fileType: "pdf",
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Send document for e-signing</DialogTitle>
          <DialogDescription>
            Dokobit uploads the file by URL, then emails or shows a signing link for each signer.
            The opportunity advances to <strong>Won</strong> when everyone has signed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Existing document (optional)</Label>
              <Select value={documentId} onValueChange={handleDocumentSelect}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Pick one or use a manual URL" />
                </SelectTrigger>
                <SelectContent>
                  {documents
                    .filter((d) => d.url)
                    .map((d) => (
                      <SelectItem key={d.id} value={d.id}>
                        {d.name}
                      </SelectItem>
                    ))}
                  {documents.filter((d) => d.url).length === 0 ? (
                    <div className="px-2 py-1.5 text-xs text-muted-foreground">
                      No attached documents with links
                    </div>
                  ) : null}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Signing UI language</Label>
              <Select value={language} onValueChange={(v) => setLanguage(v as "lt" | "en")}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lt">Lithuanian</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Document name</Label>
            <Input
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder="Service agreement"
            />
          </div>

          <div className="space-y-1.5">
            <Label>PDF URL (publicly fetchable by Dokobit)</Label>
            <Input
              type="url"
              value={sourceFileUrl}
              onChange={(e) => setSourceFileUrl(e.target.value)}
              placeholder="https://…/contract.pdf"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Signers</Label>
              <Button
                size="sm"
                variant="outline"
                className="h-7 gap-1 text-xs"
                onClick={() =>
                  setSigners((curr) => [
                    ...curr,
                    { id: "", name: "", surname: "", email: "", phone: "" },
                  ])
                }
              >
                <Plus className="h-3 w-3" />
                Add signer
              </Button>
            </div>
            <div className="space-y-2">
              {signers.map((s, i) => (
                <div
                  key={i}
                  className="grid grid-cols-1 gap-2 rounded-md border p-2 sm:grid-cols-12"
                >
                  <Input
                    className="h-8 sm:col-span-3"
                    placeholder="Personal code"
                    value={s.id}
                    onChange={(e) =>
                      setSigners((curr) =>
                        curr.map((c, idx) => (idx === i ? { ...c, id: e.target.value } : c))
                      )
                    }
                  />
                  <Input
                    className="h-8 sm:col-span-2"
                    placeholder="Name"
                    value={s.name}
                    onChange={(e) =>
                      setSigners((curr) =>
                        curr.map((c, idx) => (idx === i ? { ...c, name: e.target.value } : c))
                      )
                    }
                  />
                  <Input
                    className="h-8 sm:col-span-2"
                    placeholder="Surname"
                    value={s.surname}
                    onChange={(e) =>
                      setSigners((curr) =>
                        curr.map((c, idx) => (idx === i ? { ...c, surname: e.target.value } : c))
                      )
                    }
                  />
                  <Input
                    className="h-8 sm:col-span-3"
                    placeholder="Email (optional)"
                    type="email"
                    value={s.email}
                    onChange={(e) =>
                      setSigners((curr) =>
                        curr.map((c, idx) => (idx === i ? { ...c, email: e.target.value } : c))
                      )
                    }
                  />
                  <div className="flex items-center gap-1 sm:col-span-2">
                    <Input
                      className="h-8"
                      placeholder="Phone"
                      value={s.phone}
                      onChange={(e) =>
                        setSigners((curr) =>
                          curr.map((c, idx) =>
                            idx === i ? { ...c, phone: e.target.value } : c
                          )
                        )
                      }
                    />
                    {signers.length > 1 ? (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          setSigners((curr) => curr.filter((_, idx) => idx !== i))
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={create.isPending}>
            {create.isPending ? "Sending…" : "Send for signing"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
